import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { createComment, deleteComment, updateComment } from 'api/Comment';

import { iaRa } from 'lib/helpers/utility';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

export const useComment = (category, id) => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const handleSuccess = () => {
    try {
      queryClient.invalidateQueries({ queryKey: ['activities', category, id] });
    } catch (error) {
      console.error('Error updating queryClient activities', error);
    }
  };

  const { mutate: createCommentHandler, isLoading: isCreatingComment } = useCustomMutation({
    mutationFn: (params) => createComment(navigate, params),
    successMessage: 'Comment created',
    errorMessage: "Couldn't create comment. Please try again.",
    onSuccess: handleSuccess
  });

  const { mutateAsync: createReplyHandler, isLoading: isCreatingReply } = useCustomMutation({
    mutationFn: (params) => createComment(navigate, { ...params, type: 'reply' }),
    successMessage: 'Reply posted',
    errorMessage: "Couldn't post reply. Please try again.",
    onSuccess: (response) => handleSuccess(response, (activities) => activities)
  });

  const { mutateAsync: deleteCommentHandler } = useCustomMutation({
    mutationFn: (params) => deleteComment(navigate, params),
    successMessage: 'Comment deleted',
    errorMessage: "Couldn't delete comment. Please try again.",
    onSuccess: handleSuccess
  });

  const { mutateAsync: deleteReplyHandler } = useCustomMutation({
    mutationFn: (params) => deleteComment(navigate, { ...params, type: 'reply' }),
    successMessage: 'Reply deleted',
    errorMessage: "Couldn't delete reply. Please try again."
  });

  const { mutate: updateCommentHandler } = useCustomMutation({
    mutationFn: (params) => updateComment(navigate, params),
    successMessage: 'Comment updated',
    errorMessage: "Couldn't update comment. Please try again.",
    onSuccess: handleSuccess
  });

  const { mutateAsync: updateReplyHandler, isLoading: isUpdatingReply } = useCustomMutation({
    mutationFn: (params) => updateComment(navigate, { ...params, type: 'reply' }),
    successMessage: 'Reply updated',
    errorMessage: "Couldn't update reply. Please try again."
  });

  const onNewCommentPostHandler = async (value, options = {}) => {
    const { parentCommentId } = options;

    if (!parentCommentId) {
      await createCommentHandler({ refId: id, refTable: category, value });
      return;
    }

    const response = await createReplyHandler({
      refId: id,
      refTable: category,
      value,
      parentCommentId
    });
    const newReply = response?.comment;

    queryClient.invalidateQueries({ queryKey: ['comment-replies', parentCommentId] });

    // update the comment-replies by adding the new reply
    queryClient.setQueryData(['comment-replies', parentCommentId], (oldData) => {
      if (!oldData?.replies || !newReply) return oldData;

      const replies = iaRa(oldData.replies);
      const isAlreadyPresent = replies.some((reply) => reply.id === newReply.id);
      if (isAlreadyPresent) return oldData;

      return { ...oldData, replies: [...replies, newReply] };
    });
  };

  const onCommentDeleteHandler = async (commentId, options = {}) => {
    const { isReply = false, parentCommentId } = options;

    if (!isReply) {
      await deleteCommentHandler({ id: commentId });
      return;
    }

    await deleteReplyHandler({ id: commentId });

    if (parentCommentId) {
      queryClient.invalidateQueries({ queryKey: ['comment-replies', parentCommentId] });
    }
  };

  const onCommentSaveHandler = async (comment, options = {}) => {
    const { isReply = false, parentCommentId } = options;

    if (!isReply) {
      await updateCommentHandler({ id: comment.id, value: comment.value });
      return;
    }

    await updateReplyHandler({ id: comment.id, value: comment.value });
    if (parentCommentId) {
      queryClient.invalidateQueries({ queryKey: ['comment-replies', parentCommentId] });
    }
  };

  return {
    isCreatingComment: isCreatingComment || isCreatingReply || isUpdatingReply,
    onNewCommentPostHandler,
    onCommentDeleteHandler,
    onCommentSaveHandler
  };
};
